/* eslint-disable @typescript-eslint/no-explicit-any */

// Inspired by https://github.com/vesln/timekeeper/

/**
 * Native Date constructor reference.
 */
const NativeDate = Date;

/**
 * Fake date time container.
 */
let travelTime: number | null = null;

/**
 * Travel start container.
 */
let startedTime: number | null = null;

/**
 * Return the elapsed time.
 */
function time() {
  // Null-checked rather than truthy-checked: a target of epoch 0 is a legitimate travel destination.
  if (travelTime === null || startedTime === null) {
    return NativeDate.now();
  }

  return travelTime + (NativeDate.now() - startedTime);
}

/**
 * `FakeDate` constructor.
 */
function FakeDate(...args: any[]) {
  if (this instanceof NativeDate) {
    if (!args.length && travelTime !== null) {
      return new NativeDate(time());
    }

    const date =
      args.length == 1 && String(args[0]) === args[0] // isString(Y)
        ? // We explicitly pass it through parse:
          new NativeDate(NativeDate.parse(args[0]))
        : // We have to manually make calls depending on argument
          new (NativeDate as any)(...args);
    // Prevent mixups with unfixed Date object
    date.constructor = NativeDate;
    return date;
  }
  return NativeDate.apply(this, args);
}

// Copy any custom methods a 3rd party library may have added
for (const key in NativeDate) {
  (FakeDate as any)[key] = (NativeDate as any)[key];
}

// Copy "native" methods explicitly; they may be non-enumerable
FakeDate.UTC = NativeDate.UTC;
FakeDate.parse = NativeDate.parse;

// Setup inheritance
FakeDate.prototype = NativeDate.prototype;
FakeDate.prototype.constructor = NativeDate;

/**
 * Replace the original now method.
 *
 */
FakeDate.now = function () {
  return time();
};

/**
 * Set current DateTime.
 */
export function timeTravel(date: Date | string | number) {
  switchToFakeDate();

  if (typeof date !== 'object') {
    date = new NativeDate(date);
  }

  travelTime = date.getTime();
  startedTime = NativeDate.now();
}

/**
 * Reset the `timekeeper` behavior.
 */
export function timeTravelReset() {
  switchToNativeDate();
  startedTime = null;
  travelTime = null;
}

/**
 * Whether the UI is currently running on a fake clock.
 */
export function isTimeTravelActive(): boolean {
  return travelTime !== null;
}

/**
 * The wall clock, unaffected by time travel. Use it only where the comparison is against something
 * the server timed — session token expiry above all: the server issues and validates `exp` on real
 * time, so judging it on a simulated clock would sign the user out of a perfectly valid session.
 *
 * `syncedWithMasterAt` is the one record field that reads it, because it is compared against the
 * server's own commit time: a stamp written from a simulated clock survives the session in the
 * browser's local store and can suppress a sync that is genuinely needed. Every other record
 * timestamp — `updatedAt`, `createdAt`, `changedAt`, `backedUpAt` — follows the UI's clock, so a
 * scenario saved while travelling carries simulated times and orders by them under last-write-wins.
 * That is the accepted cost of the feature: travel to look at the plan at another date, not to save.
 */
export function realNow(): number {
  return NativeDate.now();
}

/**
 * Milliseconds to add to a simulated timestamp to land on the wall clock. Zero while the real clock
 * is in use. Lets a telemetry pipeline re-date a whole batch of already-stamped events at once,
 * keeping the spacing between them intact.
 */
export function realTimeOffset(): number {
  return realNow() - time();
}

/**
 * URL query parameter that puts the UI on a fake clock, e.g. `?timeTravel=2026-07-20T07:00:00Z`.
 * Accepts anything `Date` parses, or epoch milliseconds as a bare digit string.
 */
export const TIME_TRAVEL_PARAM = 'timeTravel';

/**
 * Session-storage key carrying the clock across page loads in one tab.
 *
 * The clock has to outlive a single document: signing in bounces through `/signin` and back, and
 * every such hop is a real page load that drops the query string. Session storage scopes the fake
 * clock to the tab — other tabs and later sessions are on real time.
 *
 * The value is either `OFF_SENTINEL` (the user exited; do not travel again this tab session, not
 * even to a build's development default) or `<simulated instant ISO>|<real epoch ms when stored>`.
 * Storing both lets the next load add the elapsed wall time, so simulated time runs forward across
 * a reload instead of snapping back to the original target.
 */
const TIME_TRAVEL_STORAGE_KEY = 'time-travel';
const OFF_SENTINEL = 'off';

function parseTimeTravelValue(raw: string): Date | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const parsed = /^\d+$/.test(trimmed)
    ? new NativeDate(Number(trimmed))
    : new NativeDate(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Session storage is unavailable in some privacy modes; time travel is optional, so degrade quietly. */
function readStored(): string | null {
  try {
    return window.sessionStorage.getItem(TIME_TRAVEL_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(value: string): void {
  try {
    window.sessionStorage.setItem(TIME_TRAVEL_STORAGE_KEY, value);
  } catch {
    // Ignored: the clock still applies to this document, it just won't survive a navigation.
  }
}

/** The instant the stored clock has reached by now, or null if nothing usable is stored. */
function resumeStoredTarget(stored: string): Date | null {
  const [at, since] = stored.split('|');
  const anchor = at === undefined ? null : parseTimeTravelValue(at);
  const storedAtReal = Number(since);
  if (anchor === null || !Number.isFinite(storedAtReal)) return null;
  return new NativeDate(anchor.getTime() + (NativeDate.now() - storedAtReal));
}

/**
 * Put the UI on a fake clock: the one named by the `timeTravel` query parameter, the one an earlier
 * page of this tab session was on, or `developmentDefault` — in that order, unless the user has
 * exited time travel in this tab, which wins over all of them.
 *
 * Called from the client instrumentation entry point so the `Date` patch lands before any
 * application module can capture the real clock.
 */
export function initTimeTravel(developmentDefault?: Date): void {
  if (typeof window === 'undefined') return;

  const fromUrl = new URLSearchParams(window.location.search).get(
    TIME_TRAVEL_PARAM,
  );
  const urlTarget = fromUrl === null ? null : parseTimeTravelValue(fromUrl);
  if (fromUrl !== null && urlTarget === null) {
    console.warn(
      `Ignoring unparseable ${TIME_TRAVEL_PARAM} value "${fromUrl}" — expected an ISO date-time or epoch milliseconds.`,
    );
  }

  const stored = readStored();
  if (urlTarget === null && stored === OFF_SENTINEL) return;

  const target =
    urlTarget ??
    (stored === null || stored === OFF_SENTINEL
      ? null
      : resumeStoredTarget(stored)) ??
    developmentDefault ??
    null;
  if (target === null) return;

  writeStored(`${target.toISOString()}|${NativeDate.now()}`);
  timeTravel(target);
}

/** Ends time travel for the tab session. The caller reloads to rebuild the UI on the real clock. */
export function stopTimeTravel(): void {
  writeStored(OFF_SENTINEL);
  timeTravelReset();
}

/**
 * Replace the `Date` with `FakeDate`.
 */
function switchToFakeDate() {
  (window as any).Date = FakeDate;
}

/**
 * Restore the `Date` to `NativeDate`.
 */
function switchToNativeDate() {
  window.Date = NativeDate;
}
